import type { BrandId } from './brands.ts';
import { BUSINESS_TZ_OFFSET_MS } from './internal-ops-business.ts';

/**
 * `/internal/commission-rates/*` wire contracts + pure resolution math
 * (epic #1034 E5-M2, #1144): the per-brand commission rate — the rate
 * input M3's accrual multiplies verified collected cash by.
 *
 * Model (settled with the epic): commission = n% of verified collected
 * cash, per BRAND, EFFECTIVE-DATED, and APPEND-ONLY (like model_pricing).
 * The settlement period is a calendar MONTH in UTC+8, so a rate's
 * effective window is keyed to a month, not an arbitrary instant:
 *
 *   effective_from = the UTC+8 calendar-month key 'YYYY-MM' this rate
 *                    starts applying from (inclusive).
 *
 * The effective rate for a period P (also a 'YYYY-MM') is the row with
 * the greatest effective_from <= P for that brand — a plain lexicographic
 * comparison, since zero-padded 'YYYY-MM' sorts chronologically. There is
 * NO update/delete surface: a mistaken future rate is superseded by
 * appending a later-effective row, never edited (future-accrual
 * idempotency depends on the history being immutable).
 *
 * `rate_bps` is basis points (1% = 100 bps), 0..10000 inclusive.
 */

/** Basis-point bounds for a commission rate (0% .. 100%). */
export const COMMISSION_RATE_BPS_MIN = 0;
export const COMMISSION_RATE_BPS_MAX = 10_000;

const COMMISSION_PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * The UTC+8 calendar-month key 'YYYY-MM' an epoch-ms instant falls in.
 * This is the "current period start" the retroactive gate compares
 * against, and the default target the current rate resolves for.
 */
export function commissionPeriodForInstant(nowMs: number): string {
  const utc8 = new Date(nowMs + BUSINESS_TZ_OFFSET_MS);
  const year = utc8.getUTCFullYear();
  const month = utc8.getUTCMonth() + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** True when `value` is a well-formed UTC+8 calendar-month key 'YYYY-MM'. */
export function isCommissionPeriodKey(value: unknown): value is string {
  return typeof value === 'string' && COMMISSION_PERIOD_RE.test(value);
}

/**
 * True once a statement's period has fully ended as of `nowMs` — i.e. the
 * current UTC+8 period has reached `periodEnd`, the exclusive next-month key
 * a statement stores as its `period_end`. Since zero-padded 'YYYY-MM' sorts
 * chronologically, this is a plain key comparison.
 *
 * A draft may be confirmed only once its period has ended: the accrual
 * rebuilds a live draft for the CURRENT open month too, and confirming that
 * freezes the period — every later same-period receipt, order, and refund
 * clawback is then silently dropped from commission (the accrual reports
 * `skipped_confirmed` and writes nothing for the group thereafter). Pure and
 * deterministic — the single "is this period closed for confirmation" test,
 * shared by the settlement confirm gate and the ops-console button state.
 */
export function isCommissionPeriodEnded(periodEnd: string, nowMs: number): boolean {
  return commissionPeriodForInstant(nowMs) >= periodEnd;
}

/** True when `value` is an integer basis-point rate within [0, 10000]. */
export function isCommissionRateBps(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= COMMISSION_RATE_BPS_MIN &&
    value <= COMMISSION_RATE_BPS_MAX
  );
}

/**
 * Resolve the effective rate for a target period from a set of rows
 * (any order): the row with the greatest `effective_from` that is still
 * `<= period`. Returns `null` when no rate is effective yet for the
 * period (e.g. the target predates the brand's first rate). Pure and
 * deterministic — the single source of truth for "which rate applies",
 * shared by platform-api's current-rate read and M3's accrual.
 */
export function resolveCommissionRate<T extends { effective_from: string }>(
  rates: readonly T[],
  period: string,
): T | null {
  let best: T | null = null;
  for (const rate of rates) {
    if (rate.effective_from <= period && (best === null || rate.effective_from > best.effective_from)) {
      best = rate;
    }
  }
  return best;
}

/** One append-only commission-rate row for a brand. */
export interface CommissionRateRecord {
  id: string;
  brand: BrandId;
  /** Basis points (1% = 100 bps), 0..10000. */
  rate_bps: number;
  /** UTC+8 calendar-month key 'YYYY-MM' this rate starts applying from. */
  effective_from: string;
  created_by_ops_user_id: string | null;
  created_at: string;
}

export interface ListCommissionRatesResponse {
  brand: BrandId;
  /** UTC+8 current calendar-month key — the retroactive floor + the
   * period `current_rate` is resolved for. */
  current_period: string;
  /** Effective rate for `current_period`; null when none applies yet. */
  current_rate: CommissionRateRecord | null;
  /** Full append-only history for the brand, newest effective_from first. */
  rates: CommissionRateRecord[];
}

export interface CreateCommissionRateRequest {
  rate_bps: number;
  /** UTC+8 'YYYY-MM'; must be >= the current period (no retroactive changes). */
  effective_from: string;
}

export interface CreateCommissionRateResponse {
  rate: CommissionRateRecord;
}
